import { useEffect } from "react"
import { Link, useNavigate } from "react-router-dom"
import { useDispatch, useSelector } from "react-redux"
import { useTranslation } from "react-i18next"

import ModalService from "src/modals"
import { selectIsAuthenticated, selectIsStaff, selectUserId } from "src/store/user"
import { allUsers as loadAllUsers, population as loadPopulation, selectAllUsers, selectPopulation } from "src/store/common"
import { getGames, selectGames } from "src/store/game"
import { liveGameRows } from "src/utils/liveGames"
import { mapLabel } from "src/utils/mapSizes"
import { roundLengthLabel } from "src/utils/duration"

import { rankedUsers, rankingPath } from "src/utils/ranking"

import BaseLayout from "src/layouts/BaseLayout"
import HallOfFame from "src/components/HallOfFame"
import useLocalizedPath from "src/hooks/useLocalizedPath"

// The one place the first-generation art survives (owner, 2026-09-06): every unit, class and
// building plate was redrawn in the pixel style, and this plate — the green-and-red halftone
// zombie — stays because the landing page was designed around it. `images/art/` holds only it.
import zombieArt from "src/assets/images/art/home_zombie.webp"

import SaveGuestButton from "src/components/SaveGuestButton"

import "./Home.css"

export const HOME_REFRESH_MS = 60000

function Home() {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const localized = useLocalizedPath()
  const allUsers = useSelector(selectAllUsers)
  const isAuthenticated = useSelector(selectIsAuthenticated)
  const userId = useSelector(selectUserId)
  const games = useSelector(selectGames)
  const online = useSelector(selectPopulation)
  const isStaff = useSelector(selectIsStaff)

  /* The wars this account is in (owner, 2026-09-22). A live game no longer pins the player to
     it — at a day a round it held the whole account — so this is where they are found again:
     the round, who it is against, and whether this round is still waiting on you. Re-read on
     arrival, since nothing but a game's own socket updates it and none is open at home. */
  const liveGames = liveGameRows(games, userId)
  useEffect(() => {
    if (isAuthenticated) dispatch(getGames())
  }, [dispatch, isAuthenticated])

  /* The home reloads what the server owns every minute (owner, 2026-09-29): the ONLINE counter,
     the hall of fame and — signed in — the account's live games. Only while the tab is visible,
     so a page left in the background asks for nothing; arriving back at a stale one refreshes
     at once. Arrival itself reads the counter (the hall of fame came in with the boot). */
  useEffect(() => {
    const refresh = () => {
      dispatch(loadPopulation())
      dispatch(loadAllUsers())
      if (isAuthenticated) dispatch(getGames())
    }
    dispatch(loadPopulation())
    const timer = setInterval(() => {
      if (document.visibilityState === "visible") refresh()
    }, HOME_REFRESH_MS)
    const onVisible = () => {
      if (document.visibilityState === "visible") refresh()
    }
    document.addEventListener("visibilitychange", onVisible)
    return () => {
      clearInterval(timer)
      document.removeEventListener("visibilitychange", onVisible)
    }
  }, [dispatch, isAuthenticated])

  // The counter next to the server tag is the accounts the server saw in the last hour
  // (GET /online/), not the size of the roster.
  const population = online ?? "—"
  // The whole list the server sends (its top 100); the podium shows three at a time and walks on.
  const hallOfFame = rankedUsers(allUsers)

  const openRegisterModal = () => {
    ModalService.show("RegisterModal", {
      onLogin: () => {
        ModalService.hideAll()
        ModalService.show("LoginModal", {
          onRegister: openRegisterModal,
        })
      },
    })
  }

  // Every call to action is visible signed out — clicking one asks the player to register first
  // rather than hiding the game behind a single "Jugar" button.
  const whenAuthenticated = (action) => () => (isAuthenticated ? action() : openRegisterModal())

  const openCreateLobbyModal = () => ModalService.show("CreateLobbyModal")
  const openJoinLobbyModal = () => ModalService.show("JoinLobbyModal")
  const openFindLobbyModal = () => ModalService.show("FindLobbyModal")
  const openGameHistoryModal = () => ModalService.show("GameHistoryModal")
  const openWatchGamesModal = () => ModalService.show("WatchGamesModal")

  const hero = (
    <div className="home-hero">
      <section className="hero-panel">
        <div className="hero-brand">
          <span className="hero-mark">H</span>
          <span className="hero-server">{t("home.serverStatus", { population })}</span>
        </div>

        {/* Zombies first: the page's H1 is the loudest thing a crawler reads, and the game is
            called Zombies vs Humans (owner, 2026-09-19). Read the other way round it was the
            campus tag game's name, on the one line that mattered most. */}
        <div className="hero-titles">
          <h1 className="hero-title zombies">{t("home.zombies")}</h1>
          <p className="hero-versus">{t("home.versus")}</p>
          <h1 className="hero-title">{t("home.humans")}</h1>

          <p className="hero-pitch">{t("home.pitch")}</p>

          {liveGames.length > 0 && (
            <section className="live-games" aria-label={t("home.liveGames")}>
              <p className="section-label">{t("home.liveGames")}</p>
              <ul className="live-games-list">
                {liveGames.map((row) => (
                  <li key={row.id}>
                    <Link to={`/game/${row.id}`} className={`live-game${row.yourTurn ? " your-turn" : ""}`}>
                      <span className="live-game-round">{t("home.liveGameRound", { round: row.round })}</span>
                      <span className="live-game-who">
                        {row.watching
                          ? t("home.liveGameWatching", { names: row.opponents.join(", ") })
                          : t("home.liveGameVersus", { names: row.opponents.join(", ") })}
                        {row.allies.length > 0 && ` · ${t("home.liveGameWith", { names: row.allies.join(", ") })}`}
                      </span>
                      <span className="live-game-clock">
                        {roundLengthLabel(row.roundSeconds, t)} · {mapLabel(row.mapSize)}
                      </span>
                      <span className="live-game-state">
                        {row.watching
                          ? t("home.liveGameOpen")
                          : row.out
                            ? t("home.liveGameOut")
                            : row.yourTurn
                              ? t("home.liveGameYourTurn")
                              : t("home.liveGameWaiting")}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          )}

          <div className="hero-actions">
            <button id="openCreateLobbyModal" className="big" onClick={whenAuthenticated(openCreateLobbyModal)}>
              {t("home.createGame")}
            </button>
            <button id="openJoinLobbyModal" className="big quiet" onClick={whenAuthenticated(openJoinLobbyModal)}>
              {t("home.join")}
            </button>
            <button id="openFindLobbyModal" className="big quiet" onClick={whenAuthenticated(openFindLobbyModal)}>
              {t("home.findLobby")}
            </button>
          </div>

          {/* The doors: the manual, basic training and — signed in — the player's finished games.
              Neither of the first two is behind whenAuthenticated: the rules are the pitch, and a
              stranger deciding whether to register is exactly who this page is for. Basic training
              is the same offer with the buttons attached — it is scripted client-side, so it needs
              no account. The replay list is behind a door rather than on the page: it grows with
              every game played, and the hero is pinned to the viewport with no height to lend it. */}
          <div className="hero-doors">
            <Link to={localized("/how-to-play")} className="hero-door hero-how-to-play">
              {t("home.howToPlay")}
            </Link>
            <Link to={localized("/tutorial")} className="hero-door hero-tutorial">
              {t("home.tutorial")}
            </Link>
            {/* A guest's way to keep the account (owner, 2026-10-02); nothing for anybody else. */}
            <SaveGuestButton className="hero-door" />
            {isAuthenticated && (
              <button id="openGameHistoryModal" className="hero-door hero-history-button" onClick={openGameHistoryModal}>
                {t("home.historyOpen")}
              </button>
            )}
            {/* Staff watch any game in progress (owner, 2026-10-04); the server refuses anybody else. */}
            {isAuthenticated && isStaff && (
              <button id="openWatchGamesModal" className="hero-door hero-history-button" onClick={openWatchGamesModal}>
                {t("home.watchGamesOpen")}
              </button>
            )}
          </div>

          <p className="home-legal">
            <Link to={localized("/privacy")}>{t("legal.privacyLink")}</Link>
            <span aria-hidden="true"> · </span>
            <Link to={localized("/terms")}>{t("legal.termsLink")}</Link>
          </p>
        </div>

        <div className="hero-hall">
          {/* The title says what the row is and the door says it opens (owner, 2026-10-08: the label
              alone did not invite a click). */}
          <div className="hall-head">
            <p className="section-label">{t("home.hallOfFame")}</p>
            <Link to={rankingPath(null)} id="openRanking" className="hall-see-all">
              {t("home.hallSeeAll")} <span aria-hidden="true">→</span>
            </Link>
          </div>
          <HallOfFame users={hallOfFame} onPick={(user) => navigate(rankingPath(user.id))} />
        </div>
      </section>

      <section className="hero-art">
        <img src={zombieArt} alt="" aria-hidden="true" />
        <div className="hero-system">
          <p className="p-mini">{t("home.systemInfo")}</p>
          <p className="p-mini">{t("home.systemStatus", { date: new Date().toISOString().slice(0, 10) })}</p>
        </div>
      </section>
    </div>
  )

  return <BaseLayout>{hero}</BaseLayout>
}

export default Home
